// Message delivery helpers — pure, storage/timer-injected, unit-tested.
//
// Julian's rule: a message sent from the phone must reach the agent exactly
// once even if he refreshes, closes the app or loses Wi-Fi mid-send, and the UI
// must never claim a failure that did not happen. The pieces:
//
//   * SendUnconfirmedError (api.ts) — the POST got no answer from the app (network
//     error, abort, or a proxy 499/502/503/504/524). The server may well have it.
//   * The OUTBOX — every send is saved here (localStorage, per chat, 24 h, capped)
//     until the server confirms it, so a reload/app-kill mid-send can recover it
//     with the SAME userMsgId. The server dedupes on that id, so a resend of a
//     message it already has is answered "duplicate" and never delivered twice.
//   * planRecovery — what to do with an unconfirmed send once the server has been
//     asked about its id: drop it (server has it), wait (still being handled),
//     resend ONCE (only if never received, ≤ 10 min old and not resent yet),
//     remove it silently (Julian removed it), or show "Not sent — tap to retry".
//     A message the server confirmed as queued stays in the outbox ("queued")
//     until it is delivered, so a queue lost in a backend restart shows as
//     "Not delivered — tap to resend" instead of vanishing.
//   * reconcileQueue — rebuild the "queued" chip from the server's queued list.
//   * createDisconnectNotice — the "Live updates disconnected" toast only after
//     the stream has been down 8 s while the page is visible.

import type { UIMessage } from "../lib/types";

/* ------------------------------------------------------------------ */
/* Failure reasons (shown on the bubble; UserBubble picks the hint)    */
/* ------------------------------------------------------------------ */

export const DELIVERY_FAILED_REASON = "The agent didn't receive this message";
export const UNCONFIRMED_REASON = "The server never confirmed this message";
/** Confirmed queued, then neither queued nor delivered (e.g. a restart lost it). */
export const QUEUE_LOST_REASON = "The server lost this queued message";
/** Too long for the outbox and the page reloaded: only the start is kept. */
export const TOO_LONG_REASON = "Too long to keep after a reload — only the start is shown; paste it again";

/* ------------------------------------------------------------------ */
/* Outbox                                                              */
/* ------------------------------------------------------------------ */

export const OUTBOX_KEY = "rfly.ops.outbox.v1";
export const OUTBOX_TTL_MS = 24 * 60 * 60 * 1000;
export const OUTBOX_MAX = 40;
export const OUTBOX_MAX_TEXT = 100_000;
/** Text kept for a message too long for the outbox: a visible trace after a
 *  reload (shown "Not sent", never resent from this stub). */
export const OUTBOX_STUB_TEXT = 2_000;
/** Automatic sends allowed per message before it is shown failed (1 + 1 resend). */
export const MAX_AUTO_ATTEMPTS = 2;
/** A send older than this (original client send time) is never resent
 *  automatically: it is shown "Not sent — tap to retry". */
export const AUTO_RESEND_MAX_AGE_MS = 10 * 60 * 1000;

export type OutboxAttachment = Omit<NonNullable<UIMessage["userAttachments"]>[number], "blobUrl">;

export interface OutboxEntry {
  /** Earlier locally captured send must be accepted/removed before this POST. */
  predecessorId?: string;
  waitingForSend?: boolean;
  /** Stop retired this ordering barrier, but its delivery audit remains. */
  orderReleased?: boolean;
  orderStopPending?: boolean;
  cid: string;
  userMsgId: string;
  text: string;
  attachmentPaths: string[];
  userAttachments?: OutboxAttachment[];
  notify: boolean;
  /** ms epoch of the first send. */
  createdAt: number;
  /** POSTs made so far without a confirmation. */
  attempts: number;
  /** "sending": unconfirmed, may be resent once. "queued": the server
   *  confirmed it queued (kept until delivered). "failed": shown failed,
   *  never auto-sent. */
  state: "sending" | "queued" | "failed";
  /** Only the first OUTBOX_STUB_TEXT characters are stored (message too long
   *  for the outbox). Never resent from storage. */
  truncated?: boolean;
}

export type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function validEntry(value: unknown): value is OutboxEntry {
  const e = value as OutboxEntry;
  return !!e && typeof e === "object" &&
    (e.predecessorId === undefined || typeof e.predecessorId === "string" && /^[a-zA-Z0-9_-]{1,64}$/.test(e.predecessorId)) &&
    (e.waitingForSend === undefined || typeof e.waitingForSend === "boolean") &&
    (e.orderReleased === undefined || typeof e.orderReleased === "boolean") &&
    (e.orderStopPending === undefined || typeof e.orderStopPending === "boolean") &&
    typeof e.cid === "string" && /^[a-z0-9][a-z0-9_-]*$/i.test(e.cid) &&
    typeof e.userMsgId === "string" && /^[a-zA-Z0-9_-]{1,64}$/.test(e.userMsgId) &&
    typeof e.text === "string" && Array.isArray(e.attachmentPaths) &&
    e.attachmentPaths.every((p) => typeof p === "string") &&
    typeof e.createdAt === "number" && Number.isFinite(e.createdAt) &&
    typeof e.attempts === "number" && (e.state === "sending" || e.state === "queued" || e.state === "failed") &&
    (e.truncated === undefined || typeof e.truncated === "boolean");
}

/** Read the outbox, dropping malformed and expired entries. Never throws. */
export function readOutbox(storage: StorageLike | null | undefined, now: number): OutboxEntry[] {
  if (!storage) return [];
  try {
    const raw = storage.getItem(OUTBOX_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(validEntry).filter((e) => now - e.createdAt < OUTBOX_TTL_MS && e.createdAt <= now + 60_000);
  } catch {
    return [];
  }
}

/** Write the outbox (at most OUTBOX_MAX; entries already shown failed are
 *  evicted first, then the oldest). Never throws; false if storage refused. */
export function writeOutbox(storage: StorageLike | null | undefined, entries: OutboxEntry[]): boolean {
  if (!storage) return false;
  try {
    let kept = [...entries].sort((a, b) => a.createdAt - b.createdAt);
    if (kept.length > OUTBOX_MAX) {
      const evict = new Set([...kept].sort((a, b) =>
        (a.state === "failed" ? 0 : 1) - (b.state === "failed" ? 0 : 1) || a.createdAt - b.createdAt,
      ).slice(0, kept.length - OUTBOX_MAX));
      kept = kept.filter((e) => !evict.has(e));
    }
    if (kept.length === 0) storage.removeItem(OUTBOX_KEY);
    else storage.setItem(OUTBOX_KEY, JSON.stringify(kept));
    return true;
  } catch {
    return false;
  }
}

/** Insert or replace by userMsgId (pure). Oversized text is stored as a
 *  truncated stub (a trace after a reload, never resent from storage). */
export function upsertOutbox(entries: OutboxEntry[], entry: OutboxEntry): OutboxEntry[] {
  const oversized = entry.text.length > OUTBOX_MAX_TEXT;
  const clean: OutboxEntry = {
    ...entry,
    ...(oversized ? { text: entry.text.slice(0, OUTBOX_STUB_TEXT), truncated: true } : {}),
    userAttachments: entry.userAttachments?.map(({ serverPath, filename, mimeType, sizeBytes, kind }) =>
      ({ serverPath, filename, mimeType, sizeBytes, kind })),
  };
  const at = entries.findIndex((e) => e.userMsgId === entry.userMsgId);
  if (at < 0) return [...entries, clean];
  const next = [...entries];
  next[at] = clean;
  return next;
}

export function removeFromOutbox(entries: OutboxEntry[], userMsgId: string): OutboxEntry[] {
  return entries.some((e) => e.userMsgId === userMsgId) ? entries.filter((e) => e.userMsgId !== userMsgId) : entries;
}

/* ------------------------------------------------------------------ */
/* Recovery decisions                                                  */
/* ------------------------------------------------------------------ */

/** What GET /messages/:id/status reports. "removed": Julian removed it
 *  (Clear/delete chat, cancel, Stop) — never delivered, drop it silently. */
export type ServerMessageState = "delivered" | "queued" | "pending" | "failed" | "unknown" | "removed";

export type RecoveryAction = "drop" | "wait" | "queued" | "resend" | "fail" | "lost" | "remove";

/**
 * Decide what to do with a send after asking the server about its id.
 *   delivered → drop    (the server has it; never resend it)
 *   removed   → remove  (Julian removed it: drop bubble/outbox silently)
 *   queued    → queued  (keep the entry, marked queued, until delivered)
 *   pending   → wait    (a POST with this id is still being handled)
 *   failed    → fail    (saved but the agent may already have it: manual only)
 *   unknown   → resend ONCE with the SAME id, only if this device holds the
 *               entry, it was never confirmed queued, never shown failed, is
 *               ≤ AUTO_RESEND_MAX_AGE_MS old and not resent yet; a message
 *               once confirmed queued is "lost" (manual resend); else "fail".
 */
export function planRecovery(
  entry: Pick<OutboxEntry, "attempts" | "state" | "createdAt" | "truncated"> | undefined,
  server: ServerMessageState,
  now = Date.now(),
): RecoveryAction {
  if (server === "delivered") return "drop";
  if (server === "removed") return "remove";
  if (server === "queued") return "queued";
  if (server === "pending") return "wait";
  if (server === "failed") return "fail";
  if (!entry) return "fail";
  if (entry.state === "queued") return "lost";
  if (entry.state === "failed" || entry.truncated) return "fail";
  if (!(now - entry.createdAt <= AUTO_RESEND_MAX_AGE_MS)) return "fail";
  return entry.attempts < MAX_AUTO_ATTEMPTS ? "resend" : "fail";
}

/** True for api.ts SendUnconfirmedError (checked by name, so it also holds
 *  across module copies and test doubles). */
/** The message route could not start the chat; nothing was saved (api.ts StartRefusedError). */
export function isStartRefused(err: unknown): err is Error & { capacity?: boolean } {
  return !!err && typeof err === "object" && (err as { name?: unknown }).name === "StartRefusedError";
}

export function isSendUnconfirmed(err: unknown): boolean {
  return !!err && typeof err === "object" && (err as { name?: unknown }).name === "SendUnconfirmedError";
}


/**
 * User bubbles for this chat's outbox entries that neither history nor the
 * server queue shows, oldest first — so a message sent just before a reload
 * never vanishes. The caller marks them unconfirmed (or failed).
 */
export function outboxBubbles(entries: OutboxEntry[], cid: string, present: Set<string>): UIMessage[] {
  return entries
    .filter((e) => e.cid === cid && !present.has(e.userMsgId))
    .sort((a, b) => a.createdAt - b.createdAt)
    .map((e) => ({
      id: e.userMsgId,
      role: "user" as const,
      content: e.text,
      createdAt: new Date(e.createdAt).toISOString(),
      ...(e.userAttachments?.length ? { userAttachments: e.userAttachments } : {}),
    }));
}

/**
 * The server's queued list is the truth for what is waiting: a user bubble or
 * failed marker for an id that is waiting there is hidden (the queued chip
 * stands for it), so one message never shows "Failed" and "queued" at once.
 */
export function hideQueuedDuplicates<F extends Record<string, unknown>>(
  messages: UIMessage[],
  failed: F,
  queuedIds: Set<string>,
): { messages: UIMessage[]; failed: F } {
  if (queuedIds.size === 0) return { messages, failed };
  const shown = messages.filter((m) => !(m.role === "user" && queuedIds.has(m.id)));
  let nextFailed = failed;
  for (const id of queuedIds) {
    if (Object.hasOwn(nextFailed, id)) {
      if (nextFailed === failed) nextFailed = { ...failed };
      delete (nextFailed as Record<string, unknown>)[id];
    }
  }
  return { messages: shown.length === messages.length ? messages : shown, failed: nextFailed };
}

/* ------------------------------------------------------------------ */
/* Queue reconciliation                                                */
/* ------------------------------------------------------------------ */

export interface ServerQueuedItem {
  userMsgId: string;
  text: string;
  truncated?: boolean;
  files: string[];
  queuedAt: number;
}

export interface QueueLike {
  userMsgId?: string;
  dispatching?: boolean;
  serverQueued?: boolean;
}

/**
 * Rebuild the local queue from the server. With the server's list: keep a
 * dispatching head (the server already moved it out), then every server item in
 * server order (reusing the local entry, which may carry thumbnails), then local
 * entries whose POST is not confirmed yet. Without it (older backend): trim to
 * the server's count as before. Returns null when nothing changes.
 */
export function reconcileQueue<Q extends QueueLike>(
  local: Q[],
  server: ServerQueuedItem[] | undefined,
  serverLength: number,
  make: (item: ServerQueuedItem) => Q,
): Q[] | null {
  const head = local[0]?.dispatching ? [local[0]] : [];
  const rest = local.slice(head.length);
  let next: Q[];
  if (Array.isArray(server)) {
    const serverIds = new Set(server.map((item) => item.userMsgId));
    const fromServer = server.map((item) => {
      const mine = rest.find((q) => q.userMsgId === item.userMsgId);
      return mine ? (mine.serverQueued ? mine : { ...mine, serverQueued: true }) : make(item);
    });
    const unconfirmed = rest.filter((q) => !q.serverQueued && !(q.userMsgId && serverIds.has(q.userMsgId)));
    next = [...head, ...fromServer, ...unconfirmed];
  } else {
    if (local.length === serverLength) return null;
    next = [...head, ...rest.slice(Math.max(0, rest.length - serverLength))];
  }
  const same = next.length === local.length && next.every((q, i) => q === local[i]);
  return same ? null : next;
}

/* ------------------------------------------------------------------ */
/* "Live updates disconnected" notice                                  */
/* ------------------------------------------------------------------ */

export const DISCONNECT_NOTICE_DELAY_MS = 8_000;
/** Transport notices (sse.ts). Shown only after the stream has been down
 *  DISCONNECT_NOTICE_DELAY_MS while visible; cleared on any (re)connect. */
export const LIVE_DISCONNECTED_MESSAGE = "Live updates disconnected. Reconnecting; your work may still be running.";
export const LIVE_CONNECTING_MESSAGE = "Connecting to live updates. Retrying…";

export interface DisconnectNoticeOptions {
  delayMs?: number;
  isVisible: () => boolean;
  setTimer: (fn: () => void, ms: number) => unknown;
  clearTimer: (timer: unknown) => void;
  show: (message: string) => void;
  /** Remove the notice if it is still on screen. */
  clear: () => void;
}

/**
 * Shows the reconnecting message only if the stream stays down for delayMs
 * while the page is visible. Any successful (re)connect — including a brand-new
 * stream opened by a resync — clears it. Hidden pages never show it; becoming
 * visible while still down starts a fresh delay.
 */
export function createDisconnectNotice(opts: DisconnectNoticeOptions) {
  const delay = opts.delayMs ?? DISCONNECT_NOTICE_DELAY_MS;
  let downMessage: string | null = null;
  let timer: unknown = null;
  const cancel = () => {
    if (timer !== null) opts.clearTimer(timer);
    timer = null;
  };
  const arm = () => {
    if (timer !== null || downMessage === null || !opts.isVisible()) return;
    timer = opts.setTimer(() => {
      timer = null;
      if (downMessage !== null && opts.isVisible()) opts.show(downMessage);
    }, delay);
  };
  return {
    down(message: string) {
      downMessage = message;
      arm();
    },
    up() {
      downMessage = null;
      cancel();
      opts.clear();
    },
    visibilityChanged() {
      if (!opts.isVisible()) cancel();
      else arm();
    },
    get isDown() {
      return downMessage !== null;
    },
  };
}

/**
 * Chips the server had confirmed as queued that a rebuild dropped (the server
 * no longer lists them). Either they drained (history shows them) or the
 * server lost them (backend restart): the caller keeps an outbox "queued"
 * entry for each so the status check tells which — never a silent drop.
 */
export function droppedServerQueued<Q extends QueueLike>(local: Q[], next: Q[]): Q[] {
  const kept = new Set(next);
  return local.filter((q) => q.serverQueued && !q.dispatching && q.userMsgId && !kept.has(q));
}
